import React, { useEffect, useMemo, useState } from "react";

import {
  Banner,
  Button,
  FormLayout,
  Modal,
  ResourceItem,
  ResourceList,
  Spinner,
  Stack,
  TextField,
  TextStyle,
} from "@shopify/polaris";

import { fetcher } from "util/fetch";

import { normalizeGroupingKey } from "./weightOverridePayloads";

/**
 * Picker modal that lists observed sub-departments or categories from the
 * latest GlobePOS import history and lets the merchant pick one (or manually
 * enter a new value) when configuring a sub-dept / category weight override.
 *
 * Spec rules (.planning/specs/2026-05-26-weight-overrides-subdept-category.md):
 * - NO caching: fetches `/api/shops/:id/pos_import_histories/latest` on every
 *   open transition.
 * - Display casing preserved as-is — the tracker writes uppercase, so picker
 *   rows render as the merchant sees them in their POS exports.
 * - Manual-entry fallback always available; required when the API returns
 *   `import_history: null` or empty `globepos_departments`.
 *
 * Props
 * -----
 * @param {boolean}  open          - Whether the modal is visible.
 * @param {string}   mode          - 'subdepartment' or 'category'.
 * @param {number}   shopId        - Used in the fetch URL.
 * @param {function} onClose       - Called with no arguments to close.
 * @param {function} onSelect      - Called with `{ key, label, code,
 *                                    parent_subdepartment? }`.  `key` is the
 *                                    normalized uppercase value, `label` is
 *                                    display value, `code` is `null` for
 *                                    picker-selected entries and a string for
 *                                    manual entries that filled the code.
 *                                    `parent_subdepartment` is included
 *                                    (as `null` in v1) only in 'category' mode.
 * @param {Array}    existingKeys  - Normalized keys already configured.  Used
 *                                    to annotate rows as "already configured";
 *                                    does NOT prevent re-selection (merchant
 *                                    may want to edit by re-picking).
 */
export default function ImportGroupingPickerModal({
  open,
  mode,
  shopId,
  onClose,
  onSelect,
  existingKeys,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [subDepartments, setSubDepartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [importHistoryId, setImportHistoryId] = useState(null);
  const [query, setQuery] = useState("");
  const [manualMode, setManualMode] = useState(false);
  const [manualLabel, setManualLabel] = useState("");
  const [manualCode, setManualCode] = useState("");

  // Fetch on every open transition — spec rule.  No caching.
  useEffect(() => {
    if (!open || !shopId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);
    setQuery("");
    setManualMode(false);
    setManualLabel("");
    setManualCode("");
    setSubDepartments([]);
    setCategories([]);
    setImportHistoryId(null);

    fetcher(`/api/shops/${shopId}/pos_import_histories/latest`)
      .then((res) => res.json())
      .then((body) => {
        if (cancelled) return;
        const importHistory = body && body.import_history;
        const departments = (body && body.globepos_departments) || {};
        setImportHistoryId(importHistory ? importHistory.id : null);
        setSubDepartments(
          Array.isArray(departments.sub_department_names)
            ? departments.sub_department_names.map((name) => ({
                name,
                code: null,
              }))
            : [],
        );
        setCategories(
          Array.isArray(departments.observed_categories)
            ? departments.observed_categories.map((description) => ({
                description,
                code: null,
                parent_subdepartment: null,
              }))
            : [],
        );
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(
          (err && err.message) ||
            "Failed to load import data. Please try again.",
        );
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, shopId]);

  // Adapt the per-mode data into a unified row shape: { key, label }.
  const allRows = useMemo(() => {
    if (mode === "category") {
      return categories.map((entry) => ({
        key: normalizeGroupingKey(entry.description),
        label: entry.description,
      }));
    }
    return subDepartments.map((entry) => ({
      key: normalizeGroupingKey(entry.name),
      label: entry.name,
    }));
  }, [mode, subDepartments, categories]);

  // Alphabetical — server already uppercases.
  const sortedRows = useMemo(
    () => [...allRows].sort((a, b) => a.label.localeCompare(b.label)),
    [allRows],
  );

  // Search filter.  Case-insensitive contains on the label.
  const filteredRows = useMemo(() => {
    const q = query.trim().toUpperCase();
    if (!q) return sortedRows;
    return sortedRows.filter((row) => row.label.toUpperCase().includes(q));
  }, [sortedRows, query]);

  const existingKeySet = useMemo(
    () => new Set((existingKeys || []).map((k) => String(k).toUpperCase())),
    [existingKeys],
  );

  const isEmpty = !loading && allRows.length === 0;

  // Show the manual form automatically when the list is empty (so merchants
  // on a fresh shop can still add entries without hunting for a toggle).
  const manualFormVisible = isEmpty || manualMode;

  const handleSelectRow = (row) => {
    const payload = { key: row.key, label: row.label, code: null };
    if (mode === "category") payload.parent_subdepartment = null;
    onSelect(payload);
  };

  const handleManualSubmit = () => {
    const trimmedLabel = manualLabel.trim();
    if (!trimmedLabel) return;
    const trimmedCode = manualCode.trim();
    onSelect({
      key: normalizeGroupingKey(trimmedLabel),
      label: trimmedLabel,
      code: trimmedCode || null,
      ...(mode === "category" ? { parent_subdepartment: null } : {}),
    });
  };

  const manualSubmitDisabled = normalizeGroupingKey(manualLabel) === "";

  const renderRow = (row) => {
    const alreadyConfigured = existingKeySet.has(row.key);
    return (
      <ResourceItem id={row.key} accessibilityLabel={`Select ${row.label}`}>
        <Stack alignment="center" distribution="equalSpacing" wrap={false}>
          <Stack.Item fill>
            <Stack vertical spacing="extraTight">
              <TextStyle variation={alreadyConfigured ? "subdued" : "strong"}>
                {row.label}
              </TextStyle>
              {alreadyConfigured && (
                <TextStyle variation="subdued">Already configured</TextStyle>
              )}
            </Stack>
          </Stack.Item>
          <Button size="slim" onClick={() => handleSelectRow(row)}>
            Select {row.label}
          </Button>
        </Stack>
      </ResourceItem>
    );
  };

  const title =
    mode === "category"
      ? "Pick a category from your latest import"
      : "Pick a sub-department from your latest import";

  return (
    <Modal
      open={open}
      title={title}
      onClose={onClose}
      secondaryActions={[{ content: "Cancel", onAction: onClose }]}
    >
      <Modal.Section>
        {loading && (
          <Stack alignment="center" spacing="tight">
            <Spinner accessibilityLabel="Loading observed groupings" size="small" />
            <TextStyle variation="subdued">Loading…</TextStyle>
          </Stack>
        )}

        {error && !loading && (
          <Banner status="critical" title="Could not load import history">
            {error}
          </Banner>
        )}

        {!loading && !error && (
          <Stack vertical spacing="loose">
            {isEmpty ? (
              <Banner status="info">
                No groupings observed yet — run a GlobePOS import to populate
                this list, or enter one manually below.
              </Banner>
            ) : (
              <>
                <TextField
                  label="Search"
                  value={query}
                  onChange={setQuery}
                  autoComplete="off"
                  placeholder={
                    mode === "category"
                      ? "Filter categories…"
                      : "Filter sub-departments…"
                  }
                  clearButton
                  onClearButtonClick={() => setQuery("")}
                />

                {filteredRows.length === 0 ? (
                  <TextStyle variation="subdued">
                    No matches for "{query}".
                  </TextStyle>
                ) : (
                  <ResourceList
                    resourceName={{
                      singular: mode === "category" ? "category" : "sub-department",
                      plural: mode === "category" ? "categories" : "sub-departments",
                    }}
                    items={filteredRows}
                    renderItem={renderRow}
                  />
                )}

                {!manualMode && (
                  <Button plain onClick={() => setManualMode(true)}>
                    Add manual entry
                  </Button>
                )}
              </>
            )}

            {manualFormVisible && (
              <Stack vertical spacing="tight">
                <TextStyle variation="strong">Manual entry</TextStyle>
                <TextStyle variation="subdued">
                  Use this when the value you need was not in the latest import
                  (e.g. a brand-new sub-department).
                </TextStyle>
                <FormLayout>
                  <TextField
                    label="Label"
                    value={manualLabel}
                    onChange={setManualLabel}
                    autoComplete="off"
                    placeholder={
                      mode === "category" ? "e.g. PASTRIES" : "e.g. CAFE"
                    }
                  />
                  <TextField
                    label="Code"
                    value={manualCode}
                    onChange={setManualCode}
                    autoComplete="off"
                    placeholder="Optional"
                    helpText="Optional SUB DEPARTMENT # or CATEGORY # — stored for display today."
                  />
                  <Button
                    primary
                    disabled={manualSubmitDisabled}
                    onClick={handleManualSubmit}
                  >
                    Add entry
                  </Button>
                </FormLayout>
              </Stack>
            )}

            {importHistoryId == null && !isEmpty && (
              <TextStyle variation="subdued">
                No import history record — values shown above came from
                a default-empty response.
              </TextStyle>
            )}
          </Stack>
        )}
      </Modal.Section>
    </Modal>
  );
}
